import { useMemo, useState } from "react";
import type { WalletAnalytics } from "../lib/api";
import { formatCurrency } from "../lib/format";
import { useElementWidth } from "../lib/useElementWidth";
import { useT } from "../lib/i18n";

/**
 * Balance vs net-deposited over time. Pure SVG, no chart library. The viewBox
 * width tracks the real rendered width so nothing stretches, and a hover
 * scrubber shows the exact numbers at any point.
 */
const HEIGHT = 220;
const PAD_X = 48;
const PAD_Y = 16;

export default function CashflowChart({ series }: { series: WalletAnalytics["series"] }) {
  const t = useT();
  const { ref, width } = useElementWidth<HTMLDivElement>(800);
  const [hover, setHover] = useState<number | null>(null);

  const geo = useMemo(() => {
    if (series.length === 0) return null;
    const values: number[] = [];
    for (const s of series) values.push(s.balance, s.net_deposited);
    const min = Math.min(0, ...values);
    const max = Math.max(...values, 0);
    const range = max - min || 1;
    const xStep = series.length > 1 ? (width - 2 * PAD_X) / (series.length - 1) : 0;
    const y = (v: number) => PAD_Y + (1 - (v - min) / range) * (HEIGHT - 2 * PAD_Y);
    const x = (i: number) => PAD_X + i * xStep;
    const balancePath = series
      .map((s, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)},${y(s.balance).toFixed(2)}`)
      .join(" ");
    const netPath = series
      .map((s, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)},${y(s.net_deposited).toFixed(2)}`)
      .join(" ");
    return { min, max, balancePath, netPath, zeroY: y(0), x, y };
  }, [series, width]);

  if (!geo || series.length === 0) {
    return (
      <p className="text-parchment-muted text-sm py-8 text-center">
        {t("dash.emptyCashflow")}
      </p>
    );
  }

  const start = series[0];
  const end = series[series.length - 1];

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / (rect.width || 1)) * width;
    const rel = (px - PAD_X) / ((width - 2 * PAD_X) || 1);
    const idx = Math.max(0, Math.min(series.length - 1, Math.round(rel * (series.length - 1))));
    setHover(idx);
  }

  const hv = hover != null ? series[hover] : null;
  const hx = hover != null ? geo.x(hover) : 0;

  return (
    <div ref={ref} className="relative">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        className="w-full"
        style={{ height: HEIGHT }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <line
          x1={PAD_X}
          x2={width - PAD_X}
          y1={geo.zeroY}
          y2={geo.zeroY}
          stroke="rgb(var(--text) / 0.15)"
          strokeDasharray="4 4"
        />
        <path
          d={geo.netPath}
          fill="none"
          stroke="rgb(var(--text-muted))"
          strokeOpacity="0.7"
          strokeWidth="1.5"
          strokeDasharray="6 4"
        />
        <path
          d={geo.balancePath}
          fill="none"
          stroke="#dcb84f"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {hv && (
          <g>
            <line x1={hx} x2={hx} y1={PAD_Y} y2={HEIGHT - PAD_Y} stroke="rgb(var(--text) / 0.25)" />
            <circle cx={hx} cy={geo.y(hv.net_deposited)} r="3" fill="rgb(var(--text-muted))" />
            <circle cx={hx} cy={geo.y(hv.balance)} r="3.5" fill="#dcb84f" />
          </g>
        )}
        <text x={PAD_X} y={12} className="fill-parchment-muted text-[10px]">
          {formatCurrency(geo.max)}
        </text>
        <text x={PAD_X} y={HEIGHT - 4} className="fill-parchment-muted text-[10px]">
          {formatCurrency(geo.min)}
        </text>
      </svg>

      {hv && (
        <div
          className="pointer-events-none absolute top-0 z-10 rounded-lg border border-glass px-3 py-2 text-xs shadow-lg"
          style={{
            left: `${Math.min(Math.max((hx / width) * 100, 10), 90)}%`,
            transform: "translateX(-50%)",
            backgroundColor: "rgb(var(--bg-950) / 0.62)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
          }}
        >
          <p className="text-parchment-muted tabular mb-1">
            {new Date(hv.ts).toLocaleDateString()}
          </p>
          <p className="flex items-center gap-3 text-parchment">
            <span className="inline-block w-3 h-0.5 bg-gold-300" />
            {t("wallet.balance")}
            <span className="tabular font-semibold ml-auto">{formatCurrency(hv.balance)}</span>
          </p>
          <p className="flex items-center gap-3 text-parchment-muted">
            <span
              className="inline-block w-3 h-0.5"
              style={{ borderTop: "1.5px dashed rgb(var(--text-muted))" }}
            />
            {t("wallet.netDeposited")}
            <span className="tabular font-semibold ml-auto">
              {formatCurrency(hv.net_deposited)}
            </span>
          </p>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-2 text-parchment">
            <span className="inline-block w-4 h-0.5 bg-gold-300" />
            {t("wallet.balance")}
          </span>
          <span className="inline-flex items-center gap-2 text-parchment-muted">
            <span
              className="inline-block w-4 h-0.5"
              style={{ borderTop: "1.5px dashed rgb(var(--text-muted))", opacity: 0.7 }}
            />
            {t("wallet.netDeposited")}
          </span>
        </div>
        <div className="text-parchment-muted tabular">
          {new Date(start.ts).toLocaleDateString()} → {new Date(end.ts).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
